import { Calendar, MapPin, User } from 'lucide-react'
import { initialFilter } from './DonationReportCard'
import type { DonationReportFilters } from './DonationReportCard'
import type { Church } from '@/features/churches/types/church'
import type { Member } from '@/features/members/types/member'
import type { reportFilterSchema } from '@/schemas/filterSchemas'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import DatePicker from '@/components/custom/date-picker'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { useCleanQueryStates } from '@/hooks'
import { parseLocalDateString, toLocalDateString } from '@/utils/timeZoneUtils'

type DonationReportFiltersProps = {
  onFilter: (
    updates:
      | Partial<{
          churchId: string
          memberId: string
          status: string
          startDate: string
          endDate: string
        }>
      | ((prev: {
          churchId: string
          memberId: string
          status: string
          startDate: string
          endDate: string
        }) => Partial<{
          churchId: string
          memberId: string
          status: string
          startDate: string
          endDate: string
        }>),
  ) => void
  goToFirstPage: () => void
  churches: Array<Church>
  members: Array<Member>
}

export function DonationReportFilters({
  onFilter,
  goToFirstPage,
  churches,
  members,
}: DonationReportFiltersProps) {
  const [filters, setFilters] =
    useCleanQueryStates<typeof reportFilterSchema>('/authed/reports')

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
        <div>
          <Label className="text-sm font-medium mb-1 block">
            <MapPin className="h-4 w-4 inline mr-1" />
            Church
          </Label>
          <Select
            value={filters.churchId || 'all'}
            onValueChange={(value) => {
              setFilters((prev) => ({
                ...prev,
                churchId: value,
                memberId: 'all',
              }))
              onFilter((prev) => ({
                ...prev,
                churchId: value,
                memberId: 'all',
              }))
              goToFirstPage()
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="All Churches" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Churches</SelectItem>
              {churches.map((church) => (
                <SelectItem key={church._id} value={church._id}>
                  {church.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="text-sm font-medium mb-1 block">
            <User className="h-4 w-4 inline mr-1" />
            Member
          </Label>
          <Select
            value={filters.memberId || 'all'}
            onValueChange={(value) => {
              setFilters((prev) => ({ ...prev, memberId: value }))
              onFilter((prev) => ({ ...prev, memberId: value }))
              goToFirstPage()
            }}
            disabled={filters.churchId === 'all'}
          >
            <SelectTrigger>
              <SelectValue placeholder="All Members" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Members</SelectItem>
              {members.map((member) => (
                <SelectItem key={member._id} value={member._id}>
                  {member.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Date pickers side by side on mobile */}
        <div className="flex flex-row gap-2 w-full col-span-1 md:col-span-2 lg:col-span-2">
          <div className="flex-1 min-w-0">
            <DatePicker
              field={{
                state: {
                  value: filters.startDate
                    ? parseLocalDateString(filters.startDate)
                    : undefined,
                  meta: {
                    isTouched: false,
                    isValid: true,
                    errors: [],
                    isValidating: false,
                  },
                },
                handleChange: (date: Date | undefined) => {
                  const stringDate = date ? toLocalDateString(date) : ''
                  setFilters((prev) => ({ ...prev, startDate: stringDate }))
                  onFilter((prev) => ({ ...prev, startDate: stringDate }))
                  goToFirstPage()
                },
                name: 'startDate',
              }}
              label={
                <>
                  <Calendar className="h-4 w-4 inline mr-1" /> Start Date
                </>
              }
              placeholder="Select date"
              showClearButton
            />
          </div>
          <div className="flex-1 min-w-0">
            <DatePicker
              field={{
                state: {
                  value: filters.endDate
                    ? parseLocalDateString(filters.endDate)
                    : undefined,
                  meta: {
                    isTouched: false,
                    isValid: true,
                    errors: [],
                    isValidating: false,
                  },
                },
                handleChange: (date: Date | undefined) => {
                  const stringDate = date ? toLocalDateString(date) : ''
                  setFilters((prev) => ({ ...prev, endDate: stringDate }))
                  onFilter((prev) => ({ ...prev, endDate: stringDate }))
                  goToFirstPage()
                },
                name: 'endDate',
              }}
              placeholder="Select date"
              label={
                <>
                  <Calendar className="h-4 w-4 inline mr-1" /> End Date
                </>
              }
              showClearButton
              minDate={
                filters.startDate
                  ? parseLocalDateString(filters.startDate)
                  : undefined
              }
            />
          </div>
        </div>

        <div>
          <Label className="text-sm font-medium mb-1 block">Status</Label>
          <Select
            value={filters.status || 'all'}
            onValueChange={(value: 'all' | 'verified' | 'pending') => {
              setFilters((prev) => ({ ...prev, status: value }))
              onFilter((prev) => ({ ...prev, status: value }))
              goToFirstPage()
            }}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="verified">Verified</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-end gap-2 mt-2">
          <Button
            type="button"
            onClick={() => {
              onFilter(initialFilter)
              setFilters(initialFilter)
              goToFirstPage()
            }}
          >
            Reset Filters
          </Button>
        </div>
      </div>
      <Separator className="col-span-1 lg:col-span-6" />
    </>
  )
}
